import './index.less';

import { CHAIN_ID } from '@derivation-tech/context';
import classNames from 'classnames';
import _ from 'lodash';
import React, { FunctionComponent, useMemo } from 'react';
import { ImageProps } from 'rebass';

import { THEME_ENUM } from '@/constants';
import { DAPP_CHAIN_CONFIGS, DEFAULT_CHAIN_ID } from '@/constants/chain';
// import { useIsNativeToken } from '@/features/chain/hook';
import { useTheme } from '@/features/global/hooks';
// import { useDappChainConfig } from '@/hooks/web3/useChain';
import { TokenInfo } from '@/types/token';

import Logo from './Logo';
import unknown from './assets/icon_crypto_default.svg?url';
import unknownD from './assets/icon_crypto_default_d.svg?url';

const BLOCKCHAIN: { [chainId: number]: string } = {
  [CHAIN_ID.ETHEREUM]: 'ethereum',
  [CHAIN_ID.GOERLI]: 'ethereum',
  [CHAIN_ID.BSC]: 'binanace',
  [CHAIN_ID.POLYGON]: 'polygon',
};

function getTokenSymbol(
  token: Omit<TokenInfo, 'id' | 'chainId'> & { chainId?: CHAIN_ID; image?: string },
  chainId?: CHAIN_ID,
): string {
  // show mapping token icon
  const mockConfig = _.get(DAPP_CHAIN_CONFIGS, [
    token.chainId || chainId || DEFAULT_CHAIN_ID,
    'network',
    'mockTokenConfig',
  ]);
  if (mockConfig?.mockTokenSymbol === token.symbol) {
    return mockConfig.mappingTokenSymbol.toLowerCase();
  }
  return token.symbol.toLowerCase();
}

export function getTokenLogoUrls(
  token: Omit<TokenInfo, 'id' | 'chainId'> & { chainId?: CHAIN_ID; image?: string },
  chainId: CHAIN_ID | undefined,
): string[] {
  const urls = [];

  const chainIdToUse = token.chainId || chainId || DEFAULT_CHAIN_ID;
  //1. get logo from icons by symbol
  urls.push(`https://api.synfutures.com/ipfs/icons/token/${getTokenSymbol(token, chainIdToUse)}.png`);
  // 2. get logo from trustwallet by chainId and address
  if (chainIdToUse in BLOCKCHAIN) {
    urls.push(
      `https://raw.githubusercontent.com/sushiswap/assets/master/blockchains/${BLOCKCHAIN[chainIdToUse]}/assets/${token.address}/logo.png`,
    );
    urls.push(
      `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${BLOCKCHAIN[chainIdToUse]}/assets/${token.address}/logo.png`,
    );
  }

  return urls;
}

interface ITokenLogoProps extends Pick<ImageProps, 'style' | 'alt' | 'className'> {
  src?: string;
  token?: Omit<TokenInfo, 'id' | 'chainId'> & { chainId?: CHAIN_ID; image?: string };
  size?: string | number;
  isHideBackground?: boolean;
  chainId?: CHAIN_ID;
  showChainIcon?: boolean;
  showBg?: boolean;
  chainIconSize?: number;
}

const TokenLogo: FunctionComponent<ITokenLogoProps> = ({
  src,
  token,
  size = 16,
  isHideBackground = false,
  className,
  chainId,
  showChainIcon,
  chainIconSize = 16,
  showBg = true,
  ...rest
}) => {
  // const isNativeToken = useIsNativeToken(token?.chainId, token);
  // const dappConfig = useDappChainConfig(token?.chainId);

  const { dataTheme } = useTheme();
  const unknownSrc = useMemo(() => (dataTheme === THEME_ENUM.DARK ? unknownD : unknown), [dataTheme]);
  const srcs = useMemo(() => {
    if (src) {
      return [src, unknownSrc];
    }
    if (!token) {
      return [unknownSrc];
    }

    // if (isNativeToken) {
    //   if (dappConfig?.network?.wrapperNativeLogo) {
    //     return [dappConfig?.network?.wrapperNativeLogo, unknownSrc];
    //   }
    // }

    if (token.address && token.symbol) {
      const defaultUrls = [...getTokenLogoUrls(token, chainId)];
      if (token.image) {
        defaultUrls.push(token.image);
      }

      return [...defaultUrls, unknownSrc];
    }
    return [];
  }, [src, token, chainId, unknownSrc]);

  return (
    <div className={classNames(!isHideBackground && 'token-icon', className)} style={{ width: size, height: size }}>
      <Logo className="token-icon_logo" srcs={srcs} width={size} height={size} {...rest} />
      {chainId && showChainIcon && (
        <div className="token-icon_chain" style={{ width: chainIconSize, height: chainIconSize }}>
          <div className="token-icon_chain-container">
            <img src={DAPP_CHAIN_CONFIGS[chainId]?.network.icon} />
            <div id="token-icon_chain-bg" style={{ width: chainIconSize + 1, height: chainIconSize + 1 }} />
          </div>
        </div>
      )}
      {showBg && (
        <div
          className="token-icon_bg"
          style={{
            width: Number(size) - 1,
            height: Number(size) - 1,
          }}
        />
      )}
    </div>
  );
};

export default React.memo(TokenLogo, (prevProps, nextProps) => {
  return prevProps.token?.symbol === nextProps.token?.symbol && nextProps.token?.address === prevProps.token?.address;
});
